import React from 'react';

export type DataTableColumn<T> = {
  key: string;
  header: string;
  className?: string;
  render: (row: T) => React.ReactNode;
};

export function DataTable<T extends { key: string }>({
  columns,
  rows,
  onRowClick,
  isRowClickable,
  emptyMessage = 'No data.',
  renderExpanded,
}: {
  columns: DataTableColumn<T>[];
  rows: T[];
  onRowClick?: (row: T) => void;
  isRowClickable?: (row: T) => boolean;
  emptyMessage?: string;
  /** Optional: when it returns non-null for a row, that content renders as a full-width
   *  row directly below it (inline expansion). Callers control which row is expanded. */
  renderExpanded?: (row: T) => React.ReactNode;
}) {
  return (
    <div className="bg-white border rounded p-4 overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead>
          <tr style={{ background: 'linear-gradient(135deg, #1a2332 0%, #2d3748 100%)' }}>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`px-3 py-2.5 text-left font-medium text-white/90 text-sm ${col.className || ''}`}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {rows.map((row) => {
            const clickable = onRowClick && (isRowClickable ? isRowClickable(row) : true);
            const expandedContent = renderExpanded ? renderExpanded(row) : null;
            return (
              <React.Fragment key={row.key}>
                <tr
                  className={`transition-all ${clickable ? 'cursor-pointer hover:bg-gray-50' : ''} ${
                    isRowClickable && !isRowClickable(row) ? 'opacity-50' : ''
                  }`}
                  style={{ boxShadow: 'inset 0 0 0 0 #01bcd4' }}
                  onMouseEnter={(e) => {
                    if (clickable) e.currentTarget.style.boxShadow = 'inset 4px 0 0 0 #01bcd4';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'inset 0 0 0 0 #01bcd4';
                  }}
                  onClick={() => {
                    if (clickable) onRowClick!(row);
                  }}
                >
                  {columns.map((col) => (
                    <td key={col.key} className={`px-3 py-3 ${col.className || ''}`}>
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
                {expandedContent && (
                  <tr>
                    <td colSpan={columns.length} className="px-3 py-3 bg-gray-50 border-l-2 border-cyan-500">
                      {expandedContent}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td className="px-3 py-4 text-gray-500" colSpan={columns.length}>
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
